import { useState, useMemo } from 'react';
import {
  Box,
  Typography,
  Collapse,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  useTheme,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { getChartColors, formatCurrency } from '../utils/chartConfig';
import MetricExplanationModal from './MetricExplanationModal';

/**
 * YearlyProjectionTable - Year-by-Year Savings Projection
 *
 * Collapsible table showing detailed year-by-year projections
 * with electricity price escalation and running ROI.
 *
 * Based on 2017 IPP Calculator table format.
 *
 * @param {Object} escalationData - Data from calculateProjectionsWithEscalation()
 * @param {number} electricityRate - Base electricity rate ($/kWh)
 */
export default function YearlyProjectionTable({
  escalationData,
  electricityRate = 0.12,
  paymentMode = 'upfront',
  treatmentCost = 0,
}) {
  const theme = useTheme();
  const colors = getChartColors(theme.palette.mode);
  const [isExpanded, setIsExpanded] = useState(true);
  const [openMetric, setOpenMetric] = useState(null);

  const tableData = useMemo(() => {
    if (!escalationData) return null;

    const { yearlyProjections, summary, parameters } = escalationData;

    // Calculate paid amount based on payment mode
    const getPaidByYear = (year) => {
      if (paymentMode === 'threeYear') {
        // 3-year: pay 1/3 each year for years 1-3
        return Math.min(year, 3) * (treatmentCost / 3);
      }
      return treatmentCost; // Upfront: full cost in Year 0
    };

    // Calculate effective rate per year with payment-mode-aware Net Position
    return yearlyProjections.map((year) => {
      const paidAmount = getPaidByYear(year.year);
      const netPosition = year.cumulative - paidAmount;
      const roi = paidAmount > 0 ? (netPosition / paidAmount) * 100 : 0;

      // For 3-year mode, always profitable from Year 1
      const prevPaid = getPaidByYear(year.year - 1);
      const prevNet = (yearlyProjections[year.year - 2]?.cumulative || 0) - prevPaid;
      const isPayback = paymentMode === 'threeYear'
        ? year.year === 1  // 3-year is always Year 1 payback
        : (netPosition >= 0 && prevNet < 0);

      return {
        ...year,
        netPosition,  // Override with payment-mode-aware value
        roi,          // Override with payment-mode-aware value
        effectiveRate: electricityRate * year.escalationMultiplier,
        isPayback,
      };
    });
  }, [escalationData, electricityRate, paymentMode, treatmentCost]);

  if (!escalationData || !tableData) {
    return null;
  }

  const { summary, parameters } = escalationData;
  const escalationPercent = (parameters.escalationRate * 100).toFixed(1);

  return (
    <Box sx={{ my: 4 }}>
      {/* Header with toggle */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          mb: 2,
          cursor: 'pointer',
          p: 2,
          borderRadius: 2,
          background: (theme) => theme.palette.mode === 'dark'
            ? 'rgba(78, 205, 196, 0.08)'
            : 'rgba(13, 115, 119, 0.06)',
          border: (theme) => `1px solid ${theme.palette.mode === 'dark'
            ? 'rgba(78, 205, 196, 0.2)'
            : 'rgba(13, 115, 119, 0.2)'}`,
          transition: 'all 0.2s ease',
          '&:hover': {
            background: (theme) => theme.palette.mode === 'dark'
              ? 'rgba(78, 205, 196, 0.12)'
              : 'rgba(13, 115, 119, 0.1)',
          },
        }}
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <Box>
          <Typography
            variant="h6"
            sx={{
              fontWeight: 700,
              letterSpacing: '-0.01em',
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              color: 'text.primary',
            }}
          >
            <TrendingUpIcon sx={{ color: (theme) => theme.palette.mode === 'dark' ? colors.teal : 'text.secondary' }} />
            Year-by-Year Projection Table
            <Chip
              label={`${escalationPercent}% Escalation`}
              size="small"
              sx={{
                ml: 1,
                background: (theme) => theme.palette.mode === 'dark' ? 'rgba(160, 206, 78, 0.2)' : 'rgba(91, 127, 36, 0.15)',
                color: (theme) => theme.palette.mode === 'dark' ? colors.primary : 'text.primary',
                fontWeight: 600,
                fontSize: '0.7rem',
              }}
            />
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Detailed annual projections with electricity price escalation
          </Typography>
        </Box>
        <IconButton size="small">
          {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        </IconButton>
      </Box>

      {/* Summary Row (always visible) */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-around',
          flexWrap: 'wrap',
          gap: 2,
          mb: 2,
          p: 2,
          borderRadius: 2,
          background: (theme) => theme.palette.mode === 'dark'
            ? 'rgba(160, 206, 78, 0.08)'
            : 'rgba(91, 127, 36, 0.06)',
          border: (theme) => `1px solid ${theme.palette.mode === 'dark'
            ? 'rgba(160, 206, 78, 0.2)'
            : 'rgba(91, 127, 36, 0.2)'}`,
        }}
      >
        <Box sx={{ textAlign: 'center', minWidth: 120 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
            <Typography variant="caption" color="text.secondary">
              With {escalationPercent}% Escalation
            </Typography>
            <IconButton
              size="small"
              onClick={() => setOpenMetric('escalation_with')}
              sx={{ p: 0.25, color: (theme) => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.4)', '&:hover': { color: colors.primary } }}
            >
              <InfoOutlinedIcon sx={{ fontSize: 12 }} />
            </IconButton>
          </Box>
          <Typography
            sx={{
              fontSize: '1.1rem',
              fontWeight: 700,
              color: (theme) => theme.palette.mode === 'dark' ? colors.primary : 'text.primary',
              fontFamily: '"JetBrains Mono", monospace',
            }}
          >
            {formatCurrency(summary.totalWithEscalation)}
          </Typography>
        </Box>
        <Box sx={{ textAlign: 'center', minWidth: 120 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
            <Typography variant="caption" color="text.secondary">
              Without Escalation
            </Typography>
            <IconButton
              size="small"
              onClick={() => setOpenMetric('escalation_without')}
              sx={{ p: 0.25, color: (theme) => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.4)', '&:hover': { color: 'text.primary' } }}
            >
              <InfoOutlinedIcon sx={{ fontSize: 12 }} />
            </IconButton>
          </Box>
          <Typography
            sx={{
              fontSize: '1.1rem',
              fontWeight: 700,
              color: (theme) => theme.palette.mode === 'dark' ? colors.grey : 'text.secondary',
              fontFamily: '"JetBrains Mono", monospace',
            }}
          >
            {formatCurrency(summary.totalWithoutEscalation)}
          </Typography>
        </Box>
        <Box sx={{ textAlign: 'center', minWidth: 120 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
            <Typography variant="caption" color="text.secondary">
              Escalation Benefit
            </Typography>
            <IconButton
              size="small"
              onClick={() => setOpenMetric('escalation_benefit')}
              sx={{ p: 0.25, color: (theme) => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.4)', '&:hover': { color: 'text.primary' } }}
            >
              <InfoOutlinedIcon sx={{ fontSize: 12 }} />
            </IconButton>
          </Box>
          <Typography
            sx={{
              fontSize: '1.1rem',
              fontWeight: 700,
              color: (theme) => theme.palette.mode === 'dark' ? colors.teal : 'text.primary',
              fontFamily: '"JetBrains Mono", monospace',
            }}
          >
            +{formatCurrency(summary.escalationBenefit)}
          </Typography>
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            (+{summary.escalationBenefitPct.toFixed(1)}%)
          </Typography>
        </Box>
        {summary.paybackYear && (
          <Box sx={{ textAlign: 'center', minWidth: 120 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
              <Typography variant="caption" color="text.secondary">
                Payback Year
              </Typography>
              <IconButton
                size="small"
                onClick={() => setOpenMetric('payback_year')}
                sx={{ p: 0.25, color: 'rgba(255,255,255,0.3)', '&:hover': { color: (theme) => theme.palette.mode === 'dark' ? '#4CAF50' : '#2E7D32' } }}
              >
                <InfoOutlinedIcon sx={{ fontSize: 12 }} />
              </IconButton>
            </Box>
            <Typography
              sx={{
                fontSize: '1.1rem',
                fontWeight: 700,
                color: (theme) => theme.palette.mode === 'dark' ? '#4CAF50' : '#2E7D32',
                fontFamily: '"JetBrains Mono", monospace',
              }}
            >
              Year {summary.paybackYear}
            </Typography>
          </Box>
        )}
      </Box>

      {/* Collapsible Table */}
      <Collapse in={isExpanded}>
        <TableContainer
          component={Paper}
          sx={{
            background: (theme) => theme.palette.mode === 'dark'
              ? 'rgba(10, 10, 10, 0.5)'
              : 'rgba(255, 255, 255, 0.95)',
            borderRadius: 2,
            border: (theme) => `1px solid ${theme.palette.mode === 'dark'
              ? 'rgba(78, 205, 196, 0.2)'
              : 'rgba(13, 115, 119, 0.3)'}`,
            '& .MuiTableCell-root': {
              borderColor: (theme) => theme.palette.mode === 'dark'
                ? 'rgba(255, 255, 255, 0.08)'
                : 'rgba(0, 0, 0, 0.12)',
              fontFamily: '"JetBrains Mono", monospace',
              fontSize: '0.8rem',
            },
          }}
        >
          <Table size="small">
            <TableHead>
              <TableRow
                sx={{
                  background: (theme) => theme.palette.mode === 'dark'
                    ? 'rgba(78, 205, 196, 0.1)'
                    : 'rgba(13, 115, 119, 0.08)',
                  '& .MuiTableCell-head': {
                    fontWeight: 700,
                    color: (theme) => theme.palette.mode === 'dark' ? colors.teal : 'text.primary',
                    fontSize: '0.75rem',
                  },
                }}
              >
                <TableCell>Year</TableCell>
                <TableCell align="right">Rate ($/kWh)</TableCell>
                <TableCell align="right">Annual Savings</TableCell>
                <TableCell align="right">Cumulative</TableCell>
                <TableCell align="right">Net Position</TableCell>
                <TableCell align="right">ROI</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {tableData.map((row) => (
                <TableRow
                  key={row.year}
                  sx={{
                    background: (theme) => row.isPayback
                      ? (theme.palette.mode === 'dark' ? 'rgba(76, 175, 80, 0.15)' : 'rgba(46, 125, 50, 0.12)')
                      : row.netPosition >= 0
                        ? (theme.palette.mode === 'dark' ? 'rgba(76, 175, 80, 0.05)' : 'rgba(46, 125, 50, 0.04)')
                        : 'transparent',
                    '&:hover': {
                      background: (theme) => theme.palette.mode === 'dark'
                        ? 'rgba(78, 205, 196, 0.08)'
                        : 'rgba(13, 115, 119, 0.06)',
                    },
                  }}
                >
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      Year {row.year}
                      {row.isPayback && (
                        <Chip
                          label="PAYBACK"
                          size="small"
                          sx={{
                            height: 18,
                            fontSize: '0.6rem',
                            fontWeight: 700,
                            background: (theme) => theme.palette.mode === 'dark' ? '#4CAF50' : '#2E7D32',
                            color: '#fff',
                          }}
                        />
                      )}
                    </Box>
                  </TableCell>
                  <TableCell align="right" sx={{ color: (theme) => theme.palette.mode === 'dark' ? colors.grey : 'text.primary' }}>
                    ${row.effectiveRate.toFixed(4)}
                    <Typography
                      variant="caption"
                      sx={{ display: 'block', color: 'text.secondary', fontSize: '0.6rem' }}
                    >
                      ({((row.escalationMultiplier - 1) * 100).toFixed(1)}% up)
                    </Typography>
                  </TableCell>
                  <TableCell align="right" sx={{ color: (theme) => theme.palette.mode === 'dark' ? colors.primary : 'text.primary', fontWeight: 600 }}>
                    {formatCurrency(row.annualValue)}
                    <Typography
                      variant="caption"
                      sx={{ display: 'block', color: 'text.secondary', fontSize: '0.6rem' }}
                    >
                      vs {formatCurrency(row.annualValueFlat)} flat
                    </Typography>
                  </TableCell>
                  <TableCell align="right" sx={{ color: (theme) => theme.palette.mode === 'dark' ? colors.teal : 'text.primary', fontWeight: 600 }}>
                    {formatCurrency(row.cumulative)}
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{
                      color: (theme) => row.netPosition >= 0
                        ? (theme.palette.mode === 'dark' ? '#4CAF50' : '#2E7D32')
                        : '#F44336',
                      fontWeight: 600,
                    }}
                  >
                    {row.netPosition >= 0 ? '+' : ''}
                    {formatCurrency(row.netPosition)}
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{
                      color: (theme) => row.roi >= 0
                        ? (theme.palette.mode === 'dark' ? colors.primary : 'text.primary')
                        : '#F44336',
                      fontWeight: 600,
                    }}
                  >
                    {row.roi >= 0 ? '+' : ''}
                    {row.roi.toFixed(0)}%
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Explanation */}
        <Box
          sx={{
            mt: 2,
            p: 2,
            borderRadius: 2,
            background: (theme) => theme.palette.mode === 'dark'
              ? 'rgba(255, 107, 53, 0.08)'
              : 'rgba(199, 70, 0, 0.06)',
            border: (theme) => `1px solid ${theme.palette.mode === 'dark'
              ? 'rgba(255, 107, 53, 0.2)'
              : 'rgba(199, 70, 0, 0.2)'}`,
          }}
        >
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            <strong>How Escalation Works:</strong> Electricity prices typically increase{' '}
            {escalationPercent}% annually (US historical average 2-4%). Year 5 savings at{' '}
            {escalationPercent}% escalation are{' '}
            <strong>{((Math.pow(1 + parameters.escalationRate, 4) - 1) * 100).toFixed(1)}%</strong>{' '}
            higher than Year 1, making treatment more valuable over time.
          </Typography>
        </Box>
      </Collapse>

      {/* Metric Explanation Modal */}
      <MetricExplanationModal
        metric={openMetric}
        open={!!openMetric}
        onClose={() => setOpenMetric(null)}
      />
    </Box>
  );
}
